import React, {
  useState,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Container, Table, Button, ScrollArea } from "@mantine/core";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { notifications } from "@mantine/notifications";
import { Validatable } from "@/pages/study/StudyStepper";
import { useDarkModeColors } from "@/hooks/helperHooks/useDarkModeColors";
import { Text } from "@/components/Generics/Text";
import { useFetchVesselParticulars } from "@/hooks/apiHooks/useFetchVesselParticulars";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import styles from "../../css/ship.module.css";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";

const VesselSizeSelection = forwardRef<Validatable, {}>((props, ref) => {
  // HOOK for Smooth transitions between pages
  const transition = usePageTransition();

  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const context = useContext(UserContext);
  const { getColor } = useDarkModeColors();
  const vesselType = context?.formValues.vesselType || "";

  const { colors, isDarkMode } = useAppColors();

  const { data, isLoading: isLoadingVesselParticulars } =
    useFetchVesselParticulars(vesselType);

  const vesselParticulars = data
    ? data.map((item: any) => item.data ?? {})
    : [];

  const handleSelect = (index: number, particular: any) => {
    setSelectedRow(index);
    context?.setFormValues((prev) => ({ ...prev, vesselSize: particular.DWT }));
  };

  const validate = () => {
    if (selectedRow === null) {
      notifications.show({
        title: "Validation Error",
        message: "Please select a vessel size.",
        color: "red",
        autoClose: 4000,
        position: "top-center",
      });
      return false;
    }
    return true;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  /*
  if (isLoadingVesselParticulars || transition)
    return (
      <Container
        fluid
        style={{ overflowY: "auto", height: "63vh" }}
      ></Container>
    );
   */

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead style={{ color: colors.secondary }}>
          <tr>
            <th className={styles.cell}>DWT</th>
            <th className={styles.cell}>LOA</th>
            <th className={styles.cell}>Beam</th>
            <th className={styles.cell}>Draft</th>
            <th className={styles.cell}>Selection</th>
          </tr>
        </thead>
        <tbody style={{ padding: "1rem" }}>
          {vesselParticulars.map((particular, index) => {
            const selected = selectedRow === index;
            const fontColor = selected
              ? isDarkMode
                ? colors.primary
                : colors.secondary
              : colors.secondary;
            return (
              <tr
                key={particular.DWT}
                style={{
                  backgroundColor: selected
                    ? colors.backgroundBrand
                    : index % 2 === 0
                      ? colors.rowA
                      : colors.rowB,
                }}
                className={styles.tableRow}
                onClick={() => handleSelect(index, particular)}
              >
                <td className={styles.cell}>
                  <Text size="small" color={fontColor}>
                    {particular.DWT}
                  </Text>
                </td>
                <td className={styles.cell}>
                  <Text size="small" color={fontColor}>
                    {particular.length_over_all_m}
                  </Text>
                </td>
                <td className={styles.cell}>
                  <Text size="small" color={fontColor}>
                    {particular.beam_m}
                  </Text>
                </td>
                <td className={styles.cell}>
                  <Text size="small" color={fontColor}>
                    {particular.draft_m}
                  </Text>
                </td>
                <td className={styles.btnCell}>
                  <div
                    onClick={() => handleSelect(index, particular)}
                    style={{
                      backgroundColor: selected
                        ? "green"
                        : isDarkMode
                          ? colors.selected
                          : colors.brand,
                      color: isDarkMode ? colors.secondary : colors.primary,
                    }}
                    className={styles.selectBtn}
                  >
                    {selected ? "Selected" : "Select"}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

export default VesselSizeSelection;
